/**
 * GalleryV2Sidebar
 *
 * Rechte Sidebar mit:
 * - Oben: Image-Preview + Metadaten + Aktionen
 * - Unten: Ordner und gespeicherte Suchen
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuthImageUrl } from '@/hooks/useAuthImageUrl';
import {
  FolderPlus,
  Folder,
  Heart,
  Search,
  X,
  Download,
  Globe,
  Layers,
  Trash2,
  ZoomIn,
  Calendar,
  Maximize2,
  Palette,
  ChevronDown,
  ChevronRight,
} from 'lucide-react';
import { toast } from 'sonner';
import { useFolders, useSavedSearches, useGallerySearch } from '@/hooks/useGalleryV2';
import { useImageActions } from '@/hooks/useImageActions';
import { Button, Input, Badge } from '@/components/v2/ui';
import { cn } from '@/lib/utils';
import type { GalleryImage } from '@/lib/api/gallery-v2';

interface GalleryV2SidebarProps {
  onClose?: () => void;
  activeImage?: GalleryImage | null;
  onOpenLightbox?: () => void;
}

export function GalleryV2Sidebar({ onClose, activeImage, onOpenLightbox }: GalleryV2SidebarProps) {
  const { t } = useTranslation();
  const { folders, createFolder, deleteFolder, addImages, isCreating, isDeleting, isLoading: isFoldersLoading, error: foldersError } = useFolders();
  const { searches } = useSavedSearches();
  const { params, updateParams } = useGallerySearch();
  const [newFolderName, setNewFolderName] = useState('');
  const [showNewFolder, setShowNewFolder] = useState(false);

  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) return;
    await createFolder({ name: newFolderName.trim() });
    setNewFolderName('');
    setShowNewFolder(false);
  };

  const handleDeleteFolder = async (folderId: string, folderName: string) => {
    if (!window.confirm(t('products.confirmFolderDelete', { name: folderName }))) {
      return;
    }
    try {
      await deleteFolder(folderId);
      toast.success(t('toast.success.folderDeleted', { folderName }));
      if (params.folder_id === folderId) {
        updateParams({ folder_id: undefined });
      }
    } catch {
      toast.error(t('toast.error.deletingFolder'));
    }
  };

  const findFolderById = (folderList: typeof folders, id: string): typeof folders[0] | undefined => {
    for (const folder of folderList) {
      if (folder.id === id) return folder;
      if (folder.children.length > 0) {
        const found = findFolderById(folder.children, id);
        if (found) return found;
      }
    }
    return undefined;
  };

  const handleAddImagesToFolder = async (folderId: string, imageIds: string[]) => {
    try {
      const folder = findFolderById(folders, folderId);
      const folderName = folder?.name || 'Ordner';
      await addImages({ folderId, imageIds });
      toast.success(t('toast.success.imageAddedToFolder', { folderName }));
    } catch {
      toast.error(t('toast.error.addingToFolder'));
    }
  };

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-neutral-200 dark:border-neutral-800">
        <h2 className="font-semibold text-neutral-900 dark:text-neutral-100">
          {activeImage ? t('common.details') : t('gallery.workspaceGallery')}
        </h2>
        {onClose && (
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X className="h-4 w-4" />
          </Button>
        )}
      </div>

      {/* Image Preview (wenn ein Bild ausgewählt ist) */}
      {activeImage && (
        <ImagePreview image={activeImage} onOpenLightbox={onOpenLightbox} />
      )}

      {/* Ordner & Suchen */}
      <div className="flex-1 overflow-y-auto">
        {/* Quick Filters */}
        <div className="p-4 space-y-1 border-b border-neutral-200 dark:border-neutral-800">
          <SidebarItem
            icon={<Search className="h-4 w-4" />}
            label={t('common.images')}
            active={!params.folder_id && !params.favorites_only}
            onClick={() => updateParams({ folder_id: undefined, favorites_only: false })}
          />
          <SidebarItem
            icon={<Heart className="h-4 w-4" />}
            label={t('common.addToFavorites')}
            active={params.favorites_only}
            onClick={() => updateParams({ favorites_only: !params.favorites_only, folder_id: undefined })}
          />
        </div>

        {/* Folders */}
        <div className="py-2">
          <div className="px-4 py-2 flex items-center justify-between">
            <span className="text-xs font-medium text-neutral-500 uppercase tracking-wide">
              Ordner
</span>
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6"
              onClick={() => setShowNewFolder(true)}
            >
              <FolderPlus className="h-4 w-4" />
            </Button>
          </div>

          {/* New Folder Input */}
          {showNewFolder && (
            <div className="px-4 pb-2">
              <div className="flex gap-2">
                <Input
                  placeholder="Ordnername"
                  value={newFolderName}
                  onChange={(e) => setNewFolderName(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleCreateFolder()}
                  autoFocus
                  className="h-8 text-sm"
                />
                <Button
                  size="sm"
                  onClick={handleCreateFolder}
                  disabled={!newFolderName.trim() || isCreating}
                >
                  OK
                </Button>
              </div>
            </div>
          )}

          {/* Folder Tree */}
          <div className="px-2">
            {isFoldersLoading ? (
              <div className="px-3 py-2 text-sm text-neutral-500">{t('common.loading')}</div>
            ) : foldersError ? (
              <div className="px-3 py-2 text-sm text-red-500">
                {t('common.error')}: {foldersError instanceof Error ? foldersError.message : 'Unbekannt'}
              </div>
            ) : folders.length === 0 ? (
              <div className="px-3 py-2 text-sm text-neutral-500">{t('common.noResults')}</div>
            ) : (
              <SimpleFolderList
                folders={folders}
                selectedId={params.folder_id}
                onSelect={(id) => updateParams({ folder_id: id, favorites_only: false })}
                onDelete={handleDeleteFolder}
                onAddImages={handleAddImagesToFolder}
                isDeleting={isDeleting}
              />
            )}
          </div>
        </div>

        {/* Saved Searches */}
        {searches.length > 0 && (
          <div className="py-2 border-t border-neutral-200 dark:border-neutral-800">
            <div className="px-4 py-2">
              <span className="text-xs font-medium text-neutral-500 uppercase tracking-wide">
                Gespeicherte Suchen
              </span>
            </div>
            <div className="px-2 pb-4">
              {searches.map((search) => (
                <SidebarItem
                  key={search.id}
                  icon={<Search className="h-4 w-4" />}
                  label={search.name}
                  count={search.usage_count}
                  onClick={() => {
                    const filters = search.filters as Record<string, unknown>;
                    updateParams({
                      q: filters.query as string,
                      tags: filters.tags as string[],
                      template_id: filters.template_id as string,
                      ratio: filters.ratio as string,
                      format: filters.format as string,
                      created_by: filters.created_by as string,
                      favorites_only: filters.favorites_only as boolean,
                      folder_id: undefined,
                    });
                  }}
                />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// =============================================================================
// Image Preview Component
// =============================================================================

interface ImagePreviewProps {
  image: GalleryImage;
  onOpenLightbox?: () => void;
}

function ImagePreview({ image, onOpenLightbox }: ImagePreviewProps) {
  const { t } = useTranslation();
  const { authUrl } = useAuthImageUrl();
  const { downloadImage, deleteImage, publishImage, generateVariation, isDeleting, isPublishing, isDownloading, isGeneratingVariation } = useImageActions();
  const displayName = image.display_name || image.prompt?.slice(0, 30) || 'Unbenannt';
  const createdDate = new Date(image.created_at).toLocaleDateString('de-DE');

  const handleDownload = () => downloadImage(image.id);

  const handleDelete = () => {
    if (window.confirm(t('gallery.deleteImageConfirm'))) {
      deleteImage(image.id);
    }
  };

  const handlePublish = () => {
    if (window.confirm(t('gallery.publishImageConfirm'))) {
      publishImage(image.id);
    }
  };

  const handleVariation = () => {
    generateVariation(image.id);
    toast.info(t('toast.info.variationCreating'));
  };

  return (
    <div className="border-b border-neutral-200 dark:border-neutral-800">
      {/* Image Thumbnail */}
      <div className="p-4">
        <div
          className="relative aspect-square rounded-lg overflow-hidden bg-neutral-100 dark:bg-neutral-800 cursor-pointer group"
          onClick={onOpenLightbox}
        >
          <img
            src={authUrl(`/thumb/${image.id}`)}
            alt={image.alt_title || displayName}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
            <ZoomIn className="h-8 w-8 text-white" />
          </div>
        </div>
      </div>

      {/* Metadaten */}
      <div className="px-4 pb-4 space-y-2">
        <p className="font-medium text-neutral-900 dark:text-neutral-100 truncate" title={displayName}>
          {displayName}
        </p>

        <div className="grid grid-cols-2 gap-2 text-sm">
          <MetaItem icon={Calendar} label="Datum" value={createdDate} />
          <MetaItem icon={Maximize2} label="Ratio" value={image.ratio || '?'} />
          {image.template_name && (
            <MetaItem icon={Palette} label="Template" value={image.template_name} className="col-span-2" />
          )}
        </div>

        {/* Tags */}
        {image.tags.length > 0 && (
          <div className="flex flex-wrap gap-1 pt-1">
            {image.tags.slice(0, 4).map((tag) => (
              <Badge key={tag.id} variant="secondary" className="text-xs">
                {tag.name}
              </Badge>
            ))}
            {image.tags.length > 4 && (
              <Badge variant="secondary" className="text-xs">
                +{image.tags.length - 4}
              </Badge>
            )}
          </div>
        )}

        {/* Public Status */}
        {image.is_public_image && (
          <div className="flex items-center gap-1.5 text-xs text-green-600 dark:text-green-400">
            <Globe className="h-3 w-3" />
            <span>{t('common.published')}</span>
          </div>
        )}
      </div>

      {/* Aktionen */}
      <div className="px-4 pb-4 grid grid-cols-2 gap-2">
        <Button size="sm" variant="outline" onClick={handleDownload} disabled={isDownloading}>
          <Download className="h-4 w-4 mr-1" />
          {t('common.download')}
        </Button>
        <Button size="sm" variant="outline" onClick={handleVariation} disabled={isGeneratingVariation}>
          <Layers className="h-4 w-4 mr-1" />
          Variation
        </Button>
        {!image.is_public_image && (
          <Button
            size="sm"
            variant="outline"
            onClick={handlePublish}
            disabled={isPublishing}
            className="text-green-600 hover:text-green-700 hover:bg-green-50 dark:text-green-400"
          >
            <Globe className="h-4 w-4 mr-1" />
            Public
          </Button>
        )}
        <Button
          size="sm"
          variant="outline"
          onClick={handleDelete}
          disabled={isDeleting}
          className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:text-red-400"
        >
          <Trash2 className="h-4 w-4 mr-1" />
          {t('common.delete')}
        </Button>
      </div>
    </div>
  );
}

// =============================================================================
// Sub-Components
// =============================================================================

interface MetaItemProps {
  icon: React.ElementType;
  label: string;
  value: string;
  className?: string;
}

function MetaItem({ icon: Icon, label, value, className }: MetaItemProps) {
  return (
    <div className={cn('flex items-center gap-1.5 text-xs text-neutral-500 dark:text-neutral-400', className)} title={label}>
      <Icon className="h-3 w-3" />
      <span className="truncate">{value}</span>
    </div>
  );
}

interface SidebarItemProps {
  icon: React.ReactNode;
  label: string;
  count?: number;
  active?: boolean;
  onClick?: () => void;
}

function SidebarItem({ icon, label, count, active, onClick }: SidebarItemProps) {
  return (
    <button
      onClick={onClick}
      className={cn(
        'w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors',
        active
          ? 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400'
          : 'text-neutral-600 hover:bg-neutral-100 dark:text-neutral-400 dark:hover:bg-neutral-800'
      )}
    >
      {icon}
      <span className="flex-1 text-left truncate">{label}</span>
      {count !== undefined && (
        <span className="text-xs text-neutral-400">{count}</span>
      )}
    </button>
  );
}

// =============================================================================
// Simple Folder List (ohne Drag & Drop)
// =============================================================================

interface SimpleFolderListProps {
  folders: typeof useFolders extends () => { folders: infer T } ? T : never;
  selectedId?: string;
  onSelect: (id: string) => void;
  onDelete: (id: string, name: string) => void;
  onAddImages: (folderId: string, imageIds: string[]) => void;
  isDeleting?: boolean;
}

function SimpleFolderList({
  folders,
  selectedId,
  onSelect,
  onDelete,
  onAddImages,
  isDeleting,
}: SimpleFolderListProps) {
  return (
    <div className="space-y-0.5">
      {folders.map((folder) => (
        <SimpleFolderItem
          key={folder.id}
          folder={folder}
          selectedId={selectedId}
          onSelect={onSelect}
          onDelete={onDelete}
          onAddImages={onAddImages}
          isDeleting={isDeleting}
          depth={0}
        />
      ))}
    </div>
  );
}

interface SimpleFolderItemProps {
  folder: {
    id: string;
    name: string;
    color: string;
    image_count: number;
    children: SimpleFolderItemProps['folder'][];
  };
  selectedId?: string;
  onSelect: (id: string) => void;
  onDelete: (id: string, name: string) => void;
  onAddImages: (folderId: string, imageIds: string[]) => void;
  isDeleting?: boolean;
  depth: number;
}

function SimpleFolderItem({
  folder,
  selectedId,
  onSelect,
  onDelete,
  onAddImages,
  isDeleting,
  depth,
}: SimpleFolderItemProps) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [isDropTarget, setIsDropTarget] = useState(false);

  const handleDragOver = (e: React.DragEvent) => {
    if (e.dataTransfer.types.includes('application/goki-image-id')) {
      e.preventDefault();
      e.dataTransfer.dropEffect = 'copy';
      setIsDropTarget(true);
    }
  };

  const handleDragLeave = () => {
    setIsDropTarget(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDropTarget(false);
    const imageId = e.dataTransfer.getData('application/goki-image-id');
    if (imageId) {
      onAddImages(folder.id, [imageId]);
    }
  };

  const hasChildren = folder.children && folder.children.length > 0;
  const isSelected = folder.id === selectedId;

  return (
    <div>
      <div
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className={cn(
          'group flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors cursor-pointer',
          isSelected
            ? 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400'
            : 'text-neutral-600 hover:bg-neutral-100 dark:text-neutral-400 dark:hover:bg-neutral-800',
          isDropTarget && 'ring-2 ring-green-400 bg-green-50 dark:bg-green-900/20'
        )}
        style={{ paddingLeft: `${12 + depth * 16}px` }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onClick={() => onSelect(folder.id)}
      >
        {hasChildren ? (
          <button
            onClick={(e) => {
              e.stopPropagation();
              setExpanded(!expanded);
            }}
            className="p-0.5 hover:bg-neutral-200 dark:hover:bg-neutral-700 rounded"
          >
            {expanded ? (
              <ChevronDown className="h-3 w-3" />
            ) : (
              <ChevronRight className="h-3 w-3" />
            )}
          </button>
        ) : (
          <span className="w-4" />
        )}

        <Folder className="h-4 w-4 flex-shrink-0" style={{ color: folder.color }} />
        <span className="flex-1 text-left truncate">{folder.name}</span>
        <span className="text-xs text-neutral-400">{folder.image_count}</span>

        {isHovered && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onDelete(folder.id, folder.name);
            }}
            disabled={isDeleting}
            className="p-1 rounded hover:bg-red-100 dark:hover:bg-red-900/30 text-neutral-400 hover:text-red-600 dark:hover:text-red-400 transition-colors"
            title={t('gallery.folderDeleteTitle')}
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        )}
      </div>

      {expanded && hasChildren && (
        <div className="mt-0.5">
          {folder.children.map((child) => (
            <SimpleFolderItem
              key={child.id}
              folder={child}
              selectedId={selectedId}
              onSelect={onSelect}
              onDelete={onDelete}
              onAddImages={onAddImages}
              isDeleting={isDeleting}
              depth={depth + 1}
            />
          ))}
        </div>
      )}
    </div>
  );
}

